import { Suspense, lazy } from 'react';
import { Navigate } from 'react-router-dom';
import { PartialRouteObject } from 'react-router';

import SidebarLayout from 'src/layouts/SidebarLayout';
import BaseLayout from 'src/layouts/BaseLayout';

import SuspenseLoader from 'src/components/SuspenseLoader';
import ScreenSkeleton from 'src/components/ScreenSkeleton';
import { ErrorFallback } from 'src/components/GlobalErrorBoundary';
import AccountSettings from './content/pages/Settings';
import AccountSettingsS3 from './content/pages/Setting';
import ProtectedRoute, { SessionMaintained } from './components/ProtectedRoute';
import { eDiscoveryUrl, Page } from './common/routes';
import {
  diagnoseChunkFailure,
  rememberFailedAttempt,
  takePreviousAttempt
} from './common/chunkDiagnostics';

// A failed dynamic import is almost always a stale chunk after a new deploy
// (Webpack hashes the chunk filenames, so the old URLs 404) or a transient
// network blip. React.lazy caches the rejected promise, so without this the
// user is stuck on the loader forever. Force a one-time hard reload to pull the
// fresh chunk; the guard prevents a reload loop if the chunk is genuinely gone.
// Reject a chunk import that hangs (e.g. a stale service worker serving a
// pending response), so the retry/reload path below can recover instead of the
// Suspense fallback spinning forever.
const CHUNK_TIMEOUT_MS = 20000;
const withChunkTimeout = (promise) =>
  Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(
        () => reject(new Error('ChunkLoadTimeout')),
        CHUNK_TIMEOUT_MS
      )
    )
  ]);

// In-app screen chunk factories, collected so we can warm them in the
// background after first paint (see `prefetchRouteChunks`). Once a chunk is
// cached, navigating to that screen mounts it synchronously — Suspense never
// falls back — so each screen's OWN skeleton is the only loading UI the user
// sees, instead of the generic full-content "Loading your workspace" loader.
//
// IMPORTANT: only in-app screens are registered here (via `lazyScreen`), NEVER
// the auth/status pages. Several auth modules (register, Awsauth, forget-
// password, account-created) clear the session — token / email / userId — at
// MODULE TOP LEVEL, on the assumption they only load when the user navigates TO
// them. Prefetching those in the background would silently log an active user
// out (blank header crash + session-expired redirect on the next refresh).
const routeChunkFactories: Array<() => Promise<any>> = [];

const lazyWithRetry = (factory) =>
  lazy(() => {
    const startedAt = Date.now();
    return withChunkTimeout(factory()).catch(async (err) => {
      // Work out WHY it failed (still downloading, server error, new deploy,
      // offline, tab asleep…) before reloading or crashing — the error alone
      // says only "ChunkLoadTimeout". Bounded to a few seconds.
      const diagnosis = await diagnoseChunkFailure(
        factory,
        err,
        startedAt,
        CHUNK_TIMEOUT_MS
      );
      const KEY = 'chunk_reload_at';
      const last = Number(window.sessionStorage.getItem(KEY) || 0);
      // Reload once, but only if we haven't reloaded within the last chunk
      // timeout window — otherwise a persistently failing/hanging chunk would
      // reload-loop. A second failure inside the window falls through to the
      // error boundary instead.
      if (Date.now() - last > CHUNK_TIMEOUT_MS + 10000) {
        window.sessionStorage.setItem(KEY, String(Date.now()));
        // Only the recovery screen reaches the activity log, so carry this
        // attempt's diagnosis over the reload in case the next one fails too.
        rememberFailedAttempt(diagnosis);
        window.location.reload();
        // Never resolve, so nothing flashes before the reload takes over.
        return new Promise(() => {});
      }
      // Already reloaded recently → let the error boundary show. It copies
      // `chunkDiagnosis` into the react_crash log entry's details.
      try {
        err.chunkDiagnosis = {
          ...diagnosis,
          firstAttempt: takePreviousAttempt()
        };
      } catch {
        /* frozen error object — the crash is still logged without details */
      }
      throw err;
    });
  });

// Retry-safe lazy import that is ALSO registered for background prefetch. Use
// ONLY for in-app screens (chunks that are safe to import while logged in).
const lazyScreen = (factory) => {
  routeChunkFactories.push(factory);
  return lazyWithRetry(factory);
};

// Warm every route chunk in the background once the app has painted, so the
// first navigation to each screen skips the Suspense download gap entirely and
// the screen mounts straight into its own skeleton. Runs at most once; failures
// are swallowed (the real navigation still carries the retry/reload guard).
let routeChunksPrefetched = false;
export const prefetchRouteChunks = () => {
  if (routeChunksPrefetched || typeof window === 'undefined') return;
  routeChunksPrefetched = true;

  const warm = () => {
    routeChunkFactories.forEach((factory, i) => {
      // Stagger so we don't fire ~40 chunk requests in one burst and starve
      // the initial data fetches.
      setTimeout(() => {
        try {
          factory().catch(() => {});
        } catch {
          /* ignore — navigation path handles real failures */
        }
      }, i * 120);
    });
  };

  const ric = (window as any).requestIdleCallback;
  if (typeof ric === 'function') {
    ric(warm, { timeout: 3000 });
  } else {
    setTimeout(warm, 1500);
  }
};

// Full-screen branded logo loader. Used only for the auth/first-paint phase,
// where it mirrors the static loader baked into public/index.html.
const Loader = (Component) => (props) =>
  (
    <Suspense fallback={<SuspenseLoader />}>
      <Component {...props} />
    </Suspense>
  );

// In-app screens' chunks are warmed in the background (see
// `prefetchRouteChunks`), so navigation almost always mounts the screen
// synchronously and its OWN skeleton is the only loading UI. On a COLD load
// (hard refresh, chunk not yet warmed) the chunk still has to download; instead
// of leaving the content area blank for that window, show a neutral content
// skeleton (NOT the branded "Loading your workspace" logo) that the screen's
// own skeleton/content replaces the instant its chunk mounts. The skeleton is
// delayed ~200ms so warm/prefetched loads don't flash it.
const Screen = (Component) => (props) =>
  (
    <Suspense fallback={<ScreenSkeleton />}>
      <Component {...props} />
    </Suspense>
  );

// Pages

const Authentication = Loader(
  lazyWithRetry(() => import('src/content/authentication/login2'))
);

const MSearchBox = Loader(
  lazyWithRetry(() => import('src/content/mbotSearch/SearchBox'))
);
const MSearchView = Loader(
  lazyWithRetry(() => import('src/content/mbotSearch/SearchView'))
);

const Register = Loader(
  lazyWithRetry(() => import('src/content/authentication/register'))
);

const DemoSignup = Loader(
  lazyWithRetry(() => import('src/content/authentication/DemoSignup'))
);

const DemoVerify = Loader(
  lazyWithRetry(() => import('src/content/authentication/DemoVerify'))
);

const AWSauth = Loader(
  lazyWithRetry(() => import('src/content/authentication/Awsauth'))
);

const AccountCreated = Loader(
  lazyWithRetry(() => import('src/content/authentication/Accountcreated'))
);

const Forgetemail = Loader(
  lazyWithRetry(
    () => import('src/content/authentication/forgetpassword/forgetcodeemail')
  )
);

const Forgetauth = Loader(
  lazyWithRetry(() => import('src/content/authentication/forgetpassword/forgetcode'))
);

const CaseSettingsS3 = Screen(
  lazyScreen(() => import('src/content/dashboard/Aboutus'))
);
const EditorialClaims = Screen(
  lazyScreen(() => import('src/content/dashboard/editorialClaims'))
);
const LoginLogs = Screen(lazyScreen(() => import('src/content/dashboard/LoginLogs')));
const UserManagement = Screen(
  lazyScreen(() => import('src/content/dashboard/UserManagement'))
);

const AppFeatures = Screen(
  lazyScreen(() => import('src/content/dashboard/appFeatures'))
);

const ActivityLogs = Screen(
  lazyScreen(() => import('src/content/dashboard/ActivityLogs'))
);

const Chatbot = Screen(
  lazyScreen(() => import('src/content/dashboard/Chatbot'))
);

const TagManagement = Screen(
  lazyScreen(() => import('src/content/dashboard/TagManagement'))
);

const AnnotateDocument = Screen(
  lazyScreen(() => import('src/content/dashboard/AnnotateDocument'))
);

const AnnotateWorkspace = Screen(
  lazyScreen(
    () => import('src/content/dashboard/AnnotateDocument/AnnotateWorkspace')
  )
);

const SmartSearchNew = Screen(
  lazyScreen(() => import('src/content/dashboard/SmartSearchNew'))
);

const LetterSearch = Screen(
  lazyScreen(() => import('src/content/dashboard/letterSearch'))
);

const ChangeLogs = Screen(
  lazyScreen(() => import('src/content/dashboard/changeLogs'))
);

const DocumentsS3 = Screen(
  lazyScreen(() => import('src/content/dashboard/Documents'))
);
const ImagePorcessS3 = Screen(
  lazyScreen(() => import('src/content/dashboard/ImageProcess'))
);
const FilesS3 = Screen(lazyScreen(() => import('src/content/dashboard/Files2')));
const ViewS3 = Screen(lazyScreen(() => import('src/content/dashboard/View')));
const ViewsS3 = Screen(lazyScreen(() => import('src/content/dashboard/Views')));
const ViewerS3 = Screen(lazyScreen(() => import('src/content/dashboard/Viewer')));
const ImageViewS3 = Screen(
  lazyScreen(() => import('src/content/dashboard/ImageView'))
);
const CopyPaste = Screen(lazyScreen(() => import('src/content/dashboard/copyPaste')));
const CopyPasteView = Screen(
  lazyScreen(() => import('src/content/dashboard/copyPasteView'))
);
const RealTime = Screen(lazyScreen(() => import('src/content/dashboard/Realtime')));
const Texract = Screen(lazyScreen(() => import('src/content/dashboard/textract')));
const ImageSearch = Screen(
  lazyScreen(() => import('src/content/dashboard/SearchImage'))
);
const SummaryListing = Screen(
  lazyScreen(() => import('src/content/dashboard/Excel'))
);
const ClaimSummary = Screen(
  lazyScreen(() => import('src/content/dashboard/ClaimSummary'))
);
const StandaloneClaim = Screen(
  lazyScreen(() => import('src/content/dashboard/StandaloneClaims'))
);
const StandaloneExamples = Screen(
  lazyScreen(() => import('src/content/dashboard/StandaloneExamples'))
);
const SemanticClaims = Screen(
  lazyScreen(() => import('src/content/dashboard/SemanticClaims'))
);
const Test = Screen(lazyScreen(() => import('src/content/dashboard/Test')));

const ClaimsDetail = Screen(
  lazyScreen(() => import('src/content/dashboard/ClaimsDetail'))
);

// Status
const Status404 = Loader(
  lazyWithRetry(() => import('src/content/pages/Status/Status404'))
);
const Status500 = Loader(
  lazyWithRetry(() => import('src/content/pages/Status/Status500'))
);
const StatusComingSoon = Loader(
  lazyWithRetry(() => import('src/content/pages/Status/ComingSoon'))
);
const StatusMaintenance = Loader(
  lazyWithRetry(() => import('src/content/pages/Status/Maintenance'))
);
// `/status/diagnostics` — the client activity log still buffered in this browser.
// Support asks a stuck user to open this and hit Download; the file describes the
// requests that failed even though none of them reached the server.
const StatusDiagnostics = Loader(
  lazyWithRetry(() => import('src/content/pages/Status/Diagnostics'))
);

// The global error boundary's recovery screen, reachable on purpose.
// `/status/error` renders it directly — for design work, screenshots, or
// showing someone what users see — while `/status/crash` throws for real, so
// the whole boundary path runs: componentDidCatch, the PostHog capture and the
// `lastGlobalError` record. Not lazy: the boundary is in the initial bundle
// already, so this costs nothing.
const StatusCrash = () => {
  throw new Error('Deliberate test crash from /status/crash');
};

const routes: PartialRouteObject[] = [
  {
    path: '*',
    element: (
      <SessionMaintained>
        <BaseLayout />
      </SessionMaintained>
    ),
    children: [
      {
        path: '/',
        element: <Authentication />
      },
      {
        path: Page.Login,
        element: <Authentication />
      },
      {
        path: 'msearch',
        children: [
          {
            path: '/',
            element: <MSearchBox />
          }
        ]
      },
      {
        path: 'mview',
        children: [
          {
            path: '/',
            element: <MSearchView />
          }
        ]
      },
      // {
      //   path: 'test',
      //   children: [
      //     {
      //       path: '/',
      //       element: <TestS3 />
      //     }
      //   ]
      // },
      {
        path: 'register',
        children: [
          {
            path: '/',
            element: <Register />
          }
        ]
      },
      {
        path: 'demo-signup',
        children: [
          {
            path: '/',
            element: <DemoSignup />
          }
        ]
      },
      {
        path: 'demo-verify',
        children: [
          {
            path: '/',
            element: <DemoVerify />
          }
        ]
      },
      {
        path: 'Awsauth',
        children: [
          {
            path: '/',
            element: <AWSauth />
          }
        ]
      },
      {
        path: 'AccountCreated',
        children: [
          {
            path: '/',
            element: <AccountCreated />
          }
        ]
      },
      {
        path: 'forget',
        children: [
          {
            path: 'password',
            element: <Forgetemail />
          },
          {
            path: 'authcode',
            element: <Forgetauth />
          }
        ]
      },
      {
        path: 'status',
        children: [
          {
            path: '/',
            element: <Navigate to="404" replace />
          },
          {
            path: '404',
            element: <Status404 />
          },
          {
            path: '500',
            element: <Status500 />
          },
          {
            path: 'maintenance',
            element: <StatusMaintenance />
          },
          {
            path: 'coming-soon',
            element: <StatusComingSoon />
          },
          {
            path: 'error',
            element: <ErrorFallback />
          },
          {
            path: 'crash',
            element: <StatusCrash />
          },
          // Registered under BOTH status blocks — the public one and the
          // signed-in one. The public route is the one that matters: the outage
          // this log exists for happens at login, when the user has no session
          // to reach a protected page with.
          {
            path: 'diagnostics',
            element: <StatusDiagnostics />
          }
        ]
      },
      {
        path: '*',
        element: <Status404 />
      }
    ]
  },
  // {
  //   path: '*',
  //   element: (
  //     <ProtectedRoute>
  //       <SidebarLayout />
  //     </ProtectedRoute>
  //   ),
  //   children: [
  //     {
  //       path: 'rules',
  //       children: [
  //         {
  //           path: '/',
  //           element: <CaseSettingsS3 />
  //         }
  //       ]
  //     }
  //   ]
  // },
  {
    path: '*',
    element: (
      <ProtectedRoute>
        <SidebarLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: eDiscoveryUrl.Rules,
        children: [
          {
            path: '/',
            element: <CaseSettingsS3 />
          }
        ]
      },
      {
        path: eDiscoveryUrl.EditorialClaims,
        children: [
          {
            path: '/',
            element: <EditorialClaims />
          }
        ]
      },
      {
        path: eDiscoveryUrl.ClaimsDetail,
        children: [
          {
            path: '/',
            element: <ClaimsDetail />
          }
        ]
      },
      {
        path: eDiscoveryUrl.ClaimSummary,
        children: [
          {
            path: '/',
            element: <ClaimSummary />
          }
        ]
      },
      {
        path: eDiscoveryUrl.StandaloneClaim,
        children: [
          {
            path: '/',
            element: <StandaloneClaim />
          }
        ]
      },
      {
        path: eDiscoveryUrl.SemanticClaims,
        children: [
          {
            path: '/',
            element: <SemanticClaims />
          }
        ]
      },
      {
        path: eDiscoveryUrl.StandaloneExamples,
        children: [
          {
            path: '/',
            element: <StandaloneExamples />
          }
        ]
      },
      {
        path: eDiscoveryUrl.LoginLogs,
        children: [
          {
            path: '/',
            element: <LoginLogs />
          }
        ]
      },
      {
        path: eDiscoveryUrl.UserManagement,
        children: [
          {
            path: '/',
            element: <UserManagement />
          }
        ]
      },
      {
        path: eDiscoveryUrl.changeLogs,
        children: [
          {
            path: '/',
            element: <ChangeLogs />
          }
        ]
      },
      {
        path: eDiscoveryUrl.appFeatures,
        children: [
          {
            path: '/',
            element: <AppFeatures />
          }
        ]
      },
      {
        path: eDiscoveryUrl.ActivityLogs,
        children: [
          {
            path: '/',
            element: <ActivityLogs />
          }
        ]
      },
      {
        path: eDiscoveryUrl.Chatbot,
        children: [
          {
            path: '/',
            element: <Chatbot />
          }
        ]
      },
      {
        path: eDiscoveryUrl.TagManagement,
        children: [
          {
            path: '/',
            element: <TagManagement />
          }
        ]
      },
      {
        path: `${eDiscoveryUrl.AnnotateDocument}/:caseid`,
        children: [
          {
            path: '/',
            element: <AnnotateDocument />
          }
        ]
      },
      {
        path: eDiscoveryUrl.SmartSearchNew,
        children: [
          {
            path: '/',
            element: <SmartSearchNew />
          }
        ]
      },
      {
        path: eDiscoveryUrl.letterSearch,
        children: [
          {
            path: '/',
            element: <LetterSearch />
          }
        ]
      }
    ]
  },

  {
    path: Page.DashboardsS3,
    element: (
      <ProtectedRoute>
        <SidebarLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: 'status',
        children: [
          {
            path: '/',
            element: <Navigate to="404" replace />
          },
          {
            path: '404',
            element: <Status404 />
          },
          {
            path: '500',
            element: <Status500 />
          },
          {
            path: 'maintenance',
            element: <StatusMaintenance />
          },
          {
            path: 'coming-soon',
            element: <StatusComingSoon />
          },
          {
            path: 'error',
            element: <ErrorFallback />
          },
          {
            path: 'crash',
            element: <StatusCrash />
          },
          // Registered under BOTH status blocks — the public one and the
          // signed-in one. The public route is the one that matters: the outage
          // this log exists for happens at login, when the user has no session
          // to reach a protected page with.
          {
            path: 'diagnostics',
            element: <StatusDiagnostics />
          }
        ]
      },
      {
        path: '/',
        element: <Navigate to={Page.CaseS3} replace />
      },
      {
        path: Page.copyPaste,
        element: <CopyPaste />
      },
      {
        path: `${Page.copyPasteView}/:userid/:cpiId/:filename`,
        element: <CopyPasteView />
      },
      {
        path: Page.RealTime,
        element: <RealTime />
      },
      {
        path: 'texract',
        element: <Texract />
      },

      {
        path: `${Page.CaseS3}/:caseid`,
        element: <DocumentsS3 />
      },
      {
        path: `${Page.ImagePorcessS3}/:caseid`,
        element: <ImagePorcessS3 />
      },

      {
        path: 'files/:caseid/:docid',
        element: <FilesS3 />
      },
      {
        path: 'view/:caseid/:docid/:filename',
        element: <ViewS3 />
      },
      {
        path: 'views/:caseid/:docid/:filename',
        element: <ViewsS3 />
      },
      {
        path: 'annotate/:caseid/:docid/:filename',
        element: <AnnotateWorkspace />
      },
      {
        path: 'viewer/:caseid/:docid/:filename',
        element: <ViewerS3 />
      },
      {
        path: 'image/view/:caseid/:docid/:filename',
        element: <ImageViewS3 />
      },
      {
        path: 'search-image/',
        element: <ImageSearch />
      },
      {
        path: Page.ClaimsDetail,
        element: <SummaryListing />
      },
      {
        path: Page.test,
        element: <Test />
      }

      // {
      //   path: `${Page.AdvanceSearch}`,
      //   element: <AdvanceSearch />
      // }
      // ,
      // {PSTPIIDocuments
      //   path: 'Test',
      //   element: <Test />
      // }
    ]
  }
];

export default routes;
